import { CloseOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Col, Form, InputNumber, Row } from "antd"
import React, { useState } from "react"

import { validateNumber } from "../../../../../utils/validation/number.ts"
import { getLeadDetails } from "../../../../Lead/api/services.ts"
import { type TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"

type SalesPriceValue = number | string | null | undefined

interface SalesPriceControlProps {
  /** Injected by the wrapping `Form.Item` — the control never talks to the
   * form directly, so its own mount is never gated on its own value (that
   * created a deadlock: an empty initial value rendered no named field at
   * all, so the field could never register to receive its initialValue). */
  value?: SalesPriceValue
  onChange?: (value: SalesPriceValue) => void
  /** Also injected by `Form.Item` (for its `<label for>`) — this component
   * nests the real input a level deeper than `Form.Item` clones onto by
   * default, so the id has to be forwarded by hand or the label goes
   * unassociated. */
  id?: string
  leadId?: string
}

function isEmptyValue(value: SalesPriceValue) {
  return value === null || value === undefined || value === ""
}

/**
 * Removability, per the backend's resolved design: the block entry itself
 * can be deleted from `blocks` (the existing generic block-delete
 * Popconfirm in `TemplateDocumentFormLabel` already does this, for free —
 * nothing here needs to touch that), but the *primary* remove/restore
 * interaction lives entirely on this field's own value, since the backend
 * renderer already no-ops on `sales_price: null` whether or not the block
 * entry is present (same convention `cost_summary`/`warranty` use).
 *
 * Clicking Remove sets the value to `null` and collapses to a "Set Sales
 * Price" affordance. Clicking that fetches the Lead's *current* value fresh
 * (never one captured once at load) and reveals the input again, prefilled
 * — or blank and editable if the Lead has none, or the lookup fails; never
 * a dead end either way. Typing the field down to blank (rather than
 * clicking Remove) intentionally does *not* collapse it — `revealed`
 * starts `true` whenever the field opens with a real value, so a user
 * mid-edit never has the control vanish out from under them.
 */
function SalesPriceControl({
  value,
  onChange,
  id,
  leadId
}: SalesPriceControlProps) {
  const [revealed, setRevealed] = useState(() => !isEmptyValue(value))
  const [restoring, setRestoring] = useState(false)

  const showRestoreAffordance = isEmptyValue(value) && !revealed

  const handleRestore = async () => {
    if (!leadId) {
      setRevealed(true)
      return
    }

    try {
      setRestoring(true)
      const res = await getLeadDetails(leadId)
      onChange?.(res.ok ? (res.data?.sales_price ?? null) : null)
    } finally {
      setRestoring(false)
      setRevealed(true)
    }
  }

  const handleClear = () => {
    onChange?.(null)
    setRevealed(false)
  }

  if (showRestoreAffordance) {
    return (
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        loading={restoring}
        onClick={handleRestore}
      >
        Set Sales Price
      </Button>
    )
  }

  return (
    <Row gutter={8} wrap={false}>
      <Col flex="auto">
        <InputNumber
          id={id}
          stringMode
          className={"w-full"}
          placeholder="0"
          suffix={"$"}
          min={0}
          max={99999999.99}
          precision={2}
          value={value as string | number | null}
          onChange={onChange}
        />
      </Col>
      <Col>
        <Button
          type="text"
          aria-label="Remove Sales Price"
          icon={<CloseOutlined />}
          onClick={handleClear}
        />
      </Col>
    </Row>
  )
}

interface SalesPriceBlockProps {
  /** Needed only to look up the Lead's current `sales_price` on Restore. */
  leadId?: string
}

/**
 * A single labeled currency field — `sales_price` is a genuinely
 * independent scalar on the Proposal/Contract, distinct from
 * `cost_summary.total` (the line-items-derived figure the comparison
 * engine reports as `contract_value`). Styled like every other plain money
 * input in this form (`CostSummaryBlock`'s line-item/discount amounts):
 * `InputNumber` with a `$` suffix and 2-decimal precision, no special
 * technical framing.
 */
function SalesPriceBlock({ leadId }: SalesPriceBlockProps) {
  return (
    <Row>
      <Col span={10}>
        <Form.Item<TemplateDocumentFormData>
          name={["sales_price"]}
          label="Sales Price"
          rules={[{ validator: validateNumber }]}
        >
          <SalesPriceControl leadId={leadId} />
        </Form.Item>
      </Col>
    </Row>
  )
}

export { SalesPriceBlock }
